import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { getTokenFromFirebase } from "./Services/firebase";
import { setChatServicesData } from "./Store/Slices/ChatServicesSlice";
import {
  clearDeviceToken,
  firebaseSelector,
  setDeviceToken,
  setNotificationStatus,
} from "./Store/Slices/FirebaseSlice";
import { jwtToken as jwtTokenAction } from "./Store/Thunks/ChatSocketThunk";
import { addUserNotificationToken } from "./Store/Thunks/FirebaseThunk";
import { getCookie } from "./Utils/cookiesHelper";
import {
  initializeWebSocketConnection,
} from "./Store/Slices/AuthSlice";
import { getUserChatData, getUserInfo, guestSession } from "./Store/Thunks/AuthThunk";
import { restoreGuestSession } from "./Store/Slices/AuthSlice";
import { loadGuestSession, tokenNamesAnotherOrg } from "./Utils/guestSession";
import StompChatService from "./Services/StompChatService";
import { handleIncomingChatMessage } from "./Services/IncomingChatMessages";
import { relayTrackedEvent } from "./Services/TrackingServices";
import Services from "./Services/Services";
import { carriedProducts, extractProductIds, productPageUrl } from "./Utils/productLinks";
import { isAgentMessage } from "./Utils/messageTypeHelper";
import { chosenStock } from "./Utils/chosenStock";
import { enableSdkBridge, notifySdkUserUpdate, subscribeSdkActions, unsubscribeSdkActions } from "./Services/SdkBridge";
import Utils, {
  getDataHandling,
  getOriginUrlAndOrgId,
  handleChatImages,
  postMessage,
} from "./Utils/utils";

import SideServicesApp from "./Components/SideServicesApp/SideServicesApp";
import AppBase from "./Components/AppBase/AppBase";
import AppContainer from "./Components/AppContainer/AppContainer";
import ServicesApp from "./Components/ServicesApp/ServicesApp";
import ErrorBoundary from "./Components/ErrorBoundary/ErrorBoundary";
import AppRouter from "./Pages/routes";
import { ThemeProvider } from "./Context/ThemeContext";
import { getResponsiveOpenWidth, getResponsiveOpenHeight, setHostViewportWidth } from "./config/widgetConfig";

import "./App.scss";

// The three moments of a streamed answer, sent out to the SDK as they happen so
// a surface outside this frame — the product dock — can show the reply being
// written rather than waiting for the last word. Named after the socket's own
// stream events, because that is what they are.
const STREAM_PHASE_START = "start";
const STREAM_PHASE_CHUNK = "chunk";
const STREAM_PHASE_END = "end";

// A message is sent under a temporary id and given its real one when the
// backend echoes it back. Written the same way the chat slice writes it.
const TEMP_ID_PREFIX = "temp_";

export default function App() {
  // Memoize URL params to prevent unnecessary re-renders
  const {
    orgId,
    originUrl,
    preview: chatPreview,
    dashboardToken,
  } = useMemo(() => getOriginUrlAndOrgId(), []);

  const dispatch = useDispatch();
  const history = useHistory();
  const { deviceToken, notificationStatus } = useSelector(firebaseSelector);
  const { isLoggedIn, isGuest, token, user, userChatData, chatId } = useSelector(
    (state) => state.auth
  );
  // The SDK message handler is registered once, so it reads sign-in state
  // through a ref; otherwise it kept the value from first render and told
  // every customer, signed in or not, to sign in.
  const authRef = useRef({ isLoggedIn, isGuest });
  useEffect(() => {
    // The token and the customer go in too: a card event has to be posted as
    // the shopper for the backend to file it against them, and this ref is what
    // the SDK's messages are answered from.
    authRef.current = { isLoggedIn, isGuest, token, user };
  }, [isLoggedIn, isGuest, token, user]);
  const {
    open,
    org,
    chatSettingsData,
    shopHas360,
    shops,
    isLoading,
    preview,
    appUrl,
    pendingMessage,
  } = useSelector((state) => state.chatServicesData);

  // Get chat socket state for smart launcher mode detection
  const {
    messages = [],
    agentsOnline = [],
    streamingMessage = null,
    historyLoads = 0,
  } = useSelector((state) => state.chatSocket || {});

  // Chat
  // Step 1: get jwt token
  // A guest token is kept in the tab's own storage, never in the sign-in
  // cookie, so it cannot be mistaken for a customer's session.
  useEffect(() => {
    const hasJwtToken = getCookie("jwtToken");
    if (token && !hasJwtToken && !isGuest) {
      dispatch(jwtTokenAction(token));
    }
  }, [token, isGuest, dispatch]);

  // Step 2: Fetch fresh user info on login (ensures profile image is up-to-date across devices)
  // Not for a guest: the backend refuses them, and the token already says who they are.
  useEffect(() => {
    if (isLoggedIn && token && !isGuest) {
      dispatch(getUserInfo());
    }
  }, [isLoggedIn, token, isGuest, dispatch]);

  // Step 3: get user chat data after login (a guest has no chat record to fetch)
  useEffect(() => {
    if (isLoggedIn && token && user?.id && !userChatData && !isGuest) {
      dispatch(getUserChatData({ userId: user.id, token }));
    }
  }, [isLoggedIn, token, user?.id, userChatData, isGuest, dispatch]);

  // A guest who reloads the shop keeps the same guest identity for the day,
  // rather than becoming a new customer on every page.
  useEffect(() => {
    if (!orgId || chatPreview || isLoggedIn) return;
    const saved = loadGuestSession(orgId);
    if (saved) dispatch(restoreGuestSession(saved));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step 4: Initialize WebSocket connection if token exists
  useEffect(() => {
    if (isLoggedIn && token) {
      dispatch(initializeWebSocketConnection());
    }
  }, [isLoggedIn, token, dispatch]);

  // Auto-connect WebSocket when token exists but user is not yet logged in (persisted session)
  useEffect(() => {
    const existingToken = getCookie("jwtToken");
    if (existingToken && !isLoggedIn) {
      StompChatService.connect(
        existingToken,
        null,
        null, // Message handlers registered via StompChatService.onMessage()
        null
      );
    }
  }, []);

  // Everything that arrives on the chat socket is taken here, for as long as
  // the widget is on the page.
  //
  // It used to be the conversation screen's job, and that screen only exists
  // while the panel is open or a question is in flight. So a question asked
  // from the card took its own listener down with it the moment it was sent,
  // and the answer — half a minute of thinking later — arrived to nobody.
  // Listening cannot depend on anything being on screen.
  //
  // The handler is registered once, so it reads who the customer is and which
  // conversation they are in through a ref rather than closing over the values
  // it happened to be born with.
  // Tell the SDK who is shopping, so what happens on the card out on the shop
  // is filed against this person — guest or signed in — instead of against a
  // browser. Ids only; the bridge keeps names and emails on this side.
  useEffect(() => {
    notifySdkUserUpdate(user?.id ? { id: user.id, isGuest } : null);
  }, [user?.id, isGuest]);

  const incomingContextRef = useRef({ user, chatId, chatSettings: chatSettingsData });
  useEffect(() => {
    incomingContextRef.current = { user, chatId, chatSettings: chatSettingsData };
  }, [user, chatId, chatSettingsData]);

  // The conversation this visitor turned out to be in, learned from the first
  // thing that arrives in it. A guest is given a chat by the backend, so the
  // widget has no other way of knowing it.
  const socketChatIdRef = useRef(null);
  useEffect(() => {
    const unsubscribe = StompChatService.onMessage((payload) => {
      const { user: currentUser, chatId: knownChatId, chatSettings } = incomingContextRef.current;
      const incoming = handleIncomingChatMessage(payload, {
        dispatch,
        user: currentUser,
        activeChatId: socketChatIdRef.current,
        fallbackChatId: knownChatId,
        chatSettings,
      });
      if (incoming?.chatId) socketChatIdRef.current = incoming.chatId;
    });
    return () => unsubscribe();
  }, [dispatch]);

  const setFirebaseToken = (value) => {
    dispatch(setDeviceToken(value));
  };

  useEffect(() => {
    if (deviceToken && token) {
      dispatch(addUserNotificationToken(deviceToken));
    }
  }, [deviceToken, token, isLoggedIn, dispatch]);

  useEffect(() => {
    if (notificationStatus === "granted") {
      getTokenFromFirebase(setFirebaseToken);
    }
    if (notificationStatus === "denied") {
      toast.error(
        "Notification is off, please allow notifications to be able to get all features"
      );
      dispatch(clearDeviceToken());
    }
  }, [notificationStatus, dispatch]);

  const fetchData = async () => {
    try {
      let { orgData, chatSettingsData, shopsData, shopHas360, guestSignInLine } =
        await getDataHandling({
          orgId,
          preview: chatPreview,
          dashboardToken,
        });

      chatSettingsData = handleChatImages({
        chatSettingsData,
        orgData,
      });

      Utils.setWidgetColors(chatSettingsData?.color);
      document.body.setAttribute("data-theme", chatSettingsData?.mode);

      dispatch(
        setChatServicesData({
          org: orgData,
          shops: shopsData,
          shopHas360,
          isLoading: false,
          appUrl: originUrl,
          chatSettingsData,
          guestSignInLine,
          preview: chatPreview,
        })
      );
      onAppInit();
      // The agent's own name, as the brand typed it in the dashboard. The SDK
      // reads a different endpoint for its config, and that one does not carry
      // it — so a shop whose organisation record is named for its web address
      // ("atelierdelile") had the card introduce the agent by that address.
      postMessage({
        type: "org",
        payload: { org: orgData, agentName: chatSettingsData?.chatBotName },
      });
      Utils.set360Cookies();
    } catch (err) {
      postMessage({
        type: "invalidOrg",
        payload: { org: err?.response?.data },
      });
      dispatch(setChatServicesData({ isLoading: false }));
    }
  };

  // Fetch data once on mount (guard against missing orgId from direct URL access)
  useEffect(() => {
    if (orgId) fetchData();
  }, [orgId]);

  // Listen for postMessage from parent window (dashboard preview)
  useEffect(() => {
    const messageHandler = (event) => {
      // Validate origin — only accept messages from the embedding parent
      if (originUrl && originUrl !== "*") {
        try {
          if (event.origin !== new URL(originUrl).origin) return;
        } catch (e) {
          return;
        }
      }

      // Parse message - handle both string and object formats
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          return; // Not JSON, skip
        }
      }

      // Skip non-object messages
      if (!data || typeof data !== 'object') return;

      if (data.type === "notification-permission-response") {
        dispatch(setNotificationStatus(data.permission));
      }

      // Handle settings update from dashboard (published)
      if (data.type === "settings-updated" || data.type === "settingsUpdated") {
        fetchData();
      }

      // Handle live preview updates (unpublished changes)
      if (data.type === "preview-settings-update") {
        if (data.payload) {
          const payloadCopy = JSON.parse(JSON.stringify(data.payload));
          const newSettings = handleChatImages({
            chatSettingsData: payloadCopy,
            orgData: org,
          });
          Utils.setWidgetColors(newSettings?.color);
          document.body.setAttribute("data-theme", newSettings?.mode);
          dispatch(setChatServicesData({ chatSettingsData: { ...newSettings } }));
        }
      }
    };

    window.addEventListener("message", messageHandler);
    return () => window.removeEventListener("message", messageHandler);
  }, [dispatch, org]);

  // Initialize WebSocket connection for settings updates (works for all users, logged in or not)
  // ONLY for live widgets, NOT for preview iframes in dashboard
  useEffect(() => {
    if (!orgId || chatPreview) return; // Skip WebSocket for preview mode

    const wsStatus = StompChatService.getConnectionStatus();
    if (wsStatus.connected) return;

    // Settings live on a public topic, so a visitor with no identity can still
    // get them — but this is the one socket the conversation uses too, and a
    // connection opened with nobody on it can receive nobody's answers. Carry
    // whatever identity is already known, the guest session saved earlier in
    // this tab included: the store's copy is not in this render yet when the
    // page has only just restored it.
    //
    // The restored copy is dropped only when it is positively somebody else's.
    // Sessions are saved per shop now, but a drawer written by an older build
    // is not, and opening this socket with the other brand's token is what made
    // an Atelier DeLile visitor into a Tseppas customer. loadGuestSession has
    // always checked the shop; it simply never got a say, being second.
    //
    // Refusing anything we merely cannot vouch for would be the wrong trade
    // here: a signed-in customer whose token we cannot read is not a customer
    // to disconnect, and this is the socket their answers arrive on.
    const saved = loadGuestSession(orgId);
    const restored = tokenNamesAnotherOrg(token, orgId) ? null : token;
    StompChatService.connect(restored || saved?.token || null, null, null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, chatPreview]);

  // Subscribe to real-time settings updates via WebSocket
  // ONLY for live widgets, NOT for preview iframes in dashboard
  useEffect(() => {
    if (!orgId || chatPreview) return; // Skip WebSocket for preview mode

    // Handler for settings update notifications
    const handleSettingsUpdate = (payload) => {
      if (payload.action === "published") {
        // Re-fetch settings when admin publishes new ones
        fetchData();
        // Notify parent window about settings update
        postMessage({ type: "settingsUpdated", payload });
      }
    };

    // Subscribe to settings updates — StompChatService handles pending
    // callbacks internally if WebSocket isn't connected yet
    const subscribeTimeout = setTimeout(() => {
      StompChatService.subscribeSettings(orgId, handleSettingsUpdate);
      subscribeSdkActions(orgId);
    }, 500);

    return () => {
      clearTimeout(subscribeTimeout);
      // Cleanup subscriptions on unmount
      StompChatService.unsubscribeSettings();
      unsubscribeSdkActions();
    };
  }, [orgId, chatPreview]);

  // Handle window resize messages from parent
  useEffect(() => {
    window.addEventListener("message", receiveMessage);
    return () => window.removeEventListener("message", receiveMessage);
  }, []);

  useEffect(() => {
    if (org?.id && preview && chatPreview) {
      dispatch(setChatServicesData({ open }));
    }

    if (open === true && window.screen.width < 900) {
      const message = { type: "setParentStyle" };
      postMessage(message);
    }

    if (!open && window.screen.width < 900) {
      const message = { type: "resetParentStyle" };
      postMessage(message);
    }
  }, [org, open]);

  // The agent's latest answer, sent out to the SDK so a surface outside this
  // frame — the product dock — can show it. Without this the dock can start a
  // conversation and then has no way to see how it was answered.
  //
  // The products go with it. An answer that names five dishes is worth far
  // more as five pictures with prices than as a paragraph mentioning them,
  // and the agent already links to each one.
  const lastAgentMessageId = useRef(null);
  // Whether the list in hand is what was already said before this frame
  // loaded: the first list it sees, or one fetched from the server. Every page
  // load starts a fresh frame, and its last stored answer used to reach the
  // card as a new one, moving the shop back to results after the shopper had
  // left them. The card still shows such an answer but does not act on it.
  const agentReplySeenLists = useRef(false);
  const agentReplyHistoryLoads = useRef(historyLoads);
  useEffect(() => {
    let cancelled = false;
    const replayed =
      !agentReplySeenLists.current || historyLoads !== agentReplyHistoryLoads.current;
    agentReplySeenLists.current = true;
    agentReplyHistoryLoads.current = historyLoads;

    const publish = async (message) => {
      const payload = { text: message.content, senderName: message.senderName };
      if (replayed) payload.replayed = true;
      // The line the agent writes for the product card, separate from the
      // answer (`cardLine: {text, suggestion: {label, message}}`), when the
      // server sends one. The card shows it instead of repeating the answer.
      if (message.cardLine) payload.cardLine = message.cardLine;
      const ids = extractProductIds(message.content);
      const carried = carriedProducts(message);

      if (carried.length) {
        // What the agent found, as the server sent it with the answer.
        payload.products = carried.map((product) => ({
          name: product.name,
          price: product.price,
          currency: org?.currency,
          image: product.image,
          url: product.url,
        }));
      } else if (ids.length) {
        try {
          const { data } = await Services.getProductsByIds(ids);
          const shopUrl = appUrl || org?.domain;
          const seen = new Set();
          payload.products = (data || [])
            .filter((product) => {
              if (seen.has(product.id)) return false;
              seen.add(product.id);
              return true;
            })
            .map((product) => ({
              name: product.name,
              price: product.price,
              currency: org?.currency,
              image: product.image_url,
              url: productPageUrl(product, shopUrl),
            }));
        } catch (e) {
          // The words are still worth sending without the pictures.
        }
      }

      if (!cancelled) postMessage({ type: "agentReply", payload });
    };

    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      // Who sent it is read the way the rest of the app reads it. This used to
      // ask for `sender.agent`, a field the socket never sends — so every
      // answer the agent gave was thrown away here and the card sat on
      // "Asking…" while the panel showed the reply.
      if (!isAgentMessage(message)) continue;
      // A frame with no words in it is a conversation event, not an answer.
      if (typeof message.content !== "string" || !message.content.trim()) continue;
      const id = message._id || message.messageId || message.createdAt;
      if (id === lastAgentMessageId.current) break;
      lastAgentMessageId.current = id;
      publish(message);
      break;
    }

    return () => { cancelled = true; };
  }, [messages, appUrl, org, historyLoads]);

  // The same answer in both places, while it is still being written. The panel
  // has always shown a streamed reply arriving word by word; the dock was told
  // only about finished messages, so a customer who asked from the card watched
  // "Asking…" for ten seconds and then got a whole paragraph at once.
  //
  // Sent as its own message rather than folded into `agentReply`, which still
  // goes out at the end with the products: a card running an older bundle hears
  // nothing new and behaves exactly as it does today.
  const streamOpen = useRef(false);
  const streamedText = useRef("");
  useEffect(() => {
    if (streamingMessage) {
      const text = streamingMessage.content || "";
      const senderName = streamingMessage.senderName;

      // Nothing written yet where something already was is the next answer
      // beginning, not the current one being erased.
      if (!streamOpen.current || (!text && streamedText.current)) {
        streamOpen.current = true;
        streamedText.current = "";
        // No words yet — this only says an answer has started, so whoever is
        // listening can stop showing the question as unanswered.
        postMessage({
          type: "agentReplyStream",
          payload: { phase: STREAM_PHASE_START, text: "", senderName },
        });
      }

      // The chunk carries everything written so far, not the new words alone,
      // so a listener that missed one is still correct after the next. An empty
      // one is not an answer arriving and is never sent as though it were.
      if (text && text !== streamedText.current) {
        streamedText.current = text;
        postMessage({
          type: "agentReplyStream",
          payload: { phase: STREAM_PHASE_CHUNK, text, senderName },
        });
      }
      return;
    }

    if (!streamOpen.current) return;
    streamOpen.current = false;
    postMessage({
      type: "agentReplyStream",
      payload: { phase: STREAM_PHASE_END, text: streamedText.current },
    });
  }, [streamingMessage]);

  // What the customer just asked, mirrored out so the card can show it. The
  // panel puts the question in the conversation the moment it is sent; the card
  // had only "Asking…" and no record of what was asked, so the two surfaces
  // disagreed about which conversation was happening.
  //
  // Only the customer's own messages: an agent's line is the answer, and it
  // already has a way out of here.
  // Which message is new is answered by identity, not by how much the list
  // grew. Counting arrivals got this wrong three ways: a history of exactly one
  // message read as a question just asked, two messages delivered in one React
  // batch mirrored neither, and a page of older messages prepended read the
  // newest message rather than the arrived one. The effect above already keys
  // on stable ids; two notions of "new" in one file is one too many.
  const seenMessageIds = useRef(null);
  const lastMirrored = useRef(null);
  const seenHistoryLoads = useRef(historyLoads);
  useEffect(() => {
    const idOf = (message) =>
      message && (message._id || message.messageId || message.createdAt);
    const ids = messages.map(idOf);

    // A list that was fetched is an old conversation, however short. One
    // message loaded and one message asked look the same from here, so the
    // store says which it was rather than this guessing.
    const loaded = historyLoads !== seenHistoryLoads.current;
    seenHistoryLoads.current = historyLoads;

    // The first list this frame ever sees is what was already said, not
    // something being said now.
    if (seenMessageIds.current === null || loaded) {
      seenMessageIds.current = new Set(ids);
      return;
    }

    const known = seenMessageIds.current;
    const newest = messages[messages.length - 1];
    const newestId = idOf(newest);
    const fresh = ids.filter((id) => id !== undefined && !known.has(id));
    ids.forEach((id) => known.add(id));

    // One message, arriving at the end, is somebody saying something now.
    // Several at once is a conversation being loaded, and a fresh id anywhere
    // but the end is a page of older messages being prepended — neither is a
    // question just asked.
    if (!newest || fresh.length !== 1 || fresh[0] !== newestId) return;
    // Who sent it is read the way the rest of the app reads it. Asking for
    // `sender.agent` — a field the socket does not send — published the agent's
    // own answer as the customer's question.
    if (isAgentMessage(newest)) return;

    const text = newest.content;
    if (typeof text !== "string" || !text.trim()) return;

    const previous = lastMirrored.current;
    // A message is sent under a temporary id and then echoed back by the
    // backend under its real one. That echo is the same question arriving
    // twice, and publishing it again would replace an answer already on its way
    // with the question that caused it. Everything else is deduplicated by id,
    // so asking the same thing twice on purpose is mirrored twice.
    if (previous) {
      if (newestId === previous.id) return;
      if (previous.temporary && text === previous.text) {
        lastMirrored.current = { id: newestId, text, temporary: false };
        return;
      }
    }

    lastMirrored.current = {
      id: newestId,
      text,
      temporary: String(newestId).startsWith(TEMP_ID_PREFIX),
    };
    postMessage({ type: "shopperMessage", payload: { text } });
  }, [messages, historyLoads]);

  // Opening the chat has to resize the frame it lives in, whoever asked for it.
  // Only the in-app toggle used to send this, so an SDK caller — the product
  // dock, or a brand calling MeetUsVR.open() — flipped the state and then had
  // its conversation happen inside a launcher-sized frame nobody could read.
  useEffect(() => {
    // Say plainly whether the conversation is open. The SDK used to infer it
    // from whatever size arrived last, which made a stale styling message sent
    // during start-up look like a close and undo the layout.
    postMessage({ type: "panelState", payload: { open: !!open } });

    if (!open) return;
    postMessage({
      type: "dimensions",
      payload: {
        width: getResponsiveOpenWidth(),
        height: getResponsiveOpenHeight(),
      },
    });
  }, [open]);

  // handle floating widget styles
  useEffect(() => {
    if (!open && chatSettingsData.widgetFloating) {
      const message = { type: "floatingWidgetStyle" };
      postMessage(message);
    }

    if (!open && !chatSettingsData.widgetFloating) {
      const message = { type: "resetFloatingWidgetStyle" };
      postMessage(message);
    }
  }, [org, chatSettingsData, open]);

  // to open on the first time on preview
  useEffect(() => {
    if (org?.id && preview && chatPreview) {
      dispatch(setChatServicesData({ open: true }));
    }
  }, [org]);

  const onAppInit = () => {
    const message = { type: "init", payload: {} };
    postMessage(message);
  };

  // The dock knows which product; the basket wants which piece of stock. One
  // lookup bridges them, and the answer goes back either way so the dock can
  // say "Added" rather than leaving the customer pressing a dead button.
  const addToCartForDock = async (payload) => {
    const reply = (result) =>
      postMessage({ type: "cartResult", payload: { requestId: payload?.requestId, ...result } });

    // A guest has a basket now. The backend refused one outright for a month —
    // 403 on reading it and on adding to it — so this refused on their behalf
    // rather than sending them to a certain failure. That is fixed on the
    // server (verified on the dev shop, 30 October 2026: a fresh guest added
    // two packs and the basket came back holding them), so the panel stops
    // deciding and asks.
    //
    // A visitor with no session at all gets one first. Pressing Add to cart is
    // often the first thing anyone does on a product page, and a shop that
    // answers that with "sign in" has lost the sale before it asked for
    // anything. Same guest identity the first question would have created.
    const { isLoggedIn: signedIn } = authRef.current;
    if (!signedIn) {
      if (!orgId) { reply({ ok: false, reason: "signin" }); return; }
      try {
        await dispatch(guestSession({ orgId })).unwrap();
      } catch (err) {
        reply({ ok: false, reason: "signin" });
        return;
      }
    }

    const [productId] = extractProductIds(payload?.url || "");
    const id = payload?.productId || productId;
    if (!id) { reply({ ok: false, reason: "unknown-product" }); return; }

    let stockId;
    try {
      const { data } = await Services.getProductsByIds([id]);
      stockId = chosenStock(data?.[0], payload?.stockId);
    } catch (err) {
      reply({ ok: false, reason: "failed" });
      return;
    }
    if (!stockId) { reply({ ok: false, reason: "no-stock" }); return; }

    try {
      await Services.addToCart({ stockId });
      reply({ ok: true });
    } catch (err) {
      // 401 is no session, 403 is the shop saying this shopper may not have a
      // basket. Either way the answer is the same one the card shows.
      //
      // Only the basket's own answer counts. The catalogue lookup above used to
      // share this catch, and the card latches "sign in" for the rest of the
      // visit once it hears it, so a 403 from the catalogue would have sat a
      // signed-in customer behind a sign-in button until they reloaded.
      const status = err?.response?.status;
      reply({ ok: false, reason: status === 401 || status === 403 ? "signin" : "failed" });
    }
  };

  const receiveMessage = (event) => {
    const { originUrl } = getOriginUrlAndOrgId();

    // Compare origin only (not full URL) to handle SDK messages from parent page
    let expectedOrigin = originUrl;
    if (originUrl && originUrl !== "*") {
      try { expectedOrigin = new URL(originUrl).origin; } catch (e) {}
    }
    if (event.origin === expectedOrigin || expectedOrigin === "*") {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.type === "windowSize") {
          const { width } = data.payload;
          // Which device this is has to be judged by the window the widget is
          // embedded in, not by the frame it lives in.
          setHostViewportWidth(width);
          dispatch(setChatServicesData({ width }));
        }
        if (data.type === "sdk-init") {
          enableSdkBridge();
          if (orgId) subscribeSdkActions(orgId);
        }
        if (data.type === "sdk-open") {
          dispatch(setChatServicesData({ open: true }));
        }
        // The card's "Sign in" / "Sign in to add": the conversation opens on
        // its sign-in screen, whether or not it was already open.
        if (data.type === "sdk-sign-in") {
          dispatch(setChatServicesData({ open: true }));
          if (!authRef.current.isLoggedIn && !history.location.pathname.startsWith("/login")) {
            history.push("/login");
          }
        }
        if (data.type === "sdk-close") {
          dispatch(setChatServicesData({ open: false }));
        }
        if (data.type === "sdk-page-context") {
          dispatch(setChatServicesData({ pageContext: data.payload }));
        }
        // The card's Voice button: open the conversation already listening.
        // A guest gets an identity the same way a typed question does.
        // The card's own voice started: the panel's stops (one voice at a time).
        if (data.type === "sdk-voice-stop") {
          dispatch(setChatServicesData({ voiceStopRequest: Date.now(), pendingVoice: false }));
        }
        if (data.type === "sdk-voice") {
          dispatch(setChatServicesData({ open: true, pendingVoice: true }));
          if (!authRef.current.isLoggedIn && orgId) {
            dispatch(guestSession({ orgId }));
          }
        }
        if (data.type === "sdk-send-message") {
          dispatch(setChatServicesData({ open: true, pendingMessage: data.payload?.content }));
          // A visitor who has not signed in gets a guest identity so the
          // question can go; the home screen sends it once that arrives.
          if (!authRef.current.isLoggedIn && orgId) {
            dispatch(guestSession({ orgId }));
          }
        }
        // Asked from the product dock, which shows the answer itself. Opening
        // the panel here would throw the shop the customer is reading behind a
        // conversation they did not ask to see.
        //
        // The conversation screen is what actually sends a pending message, and
        // it is only mounted on its own route — so route there. The frame stays
        // closed, so none of this is on screen; it just means the question is
        // sent now rather than whenever the customer next opens the panel.
        // The dock's Add to cart. It runs here because the basket belongs to a
        // signed-in customer and this is the only side that knows who they are.
        if (data.type === "sdk-add-to-cart") {
          addToCartForDock(data.payload);
        }

        // What the shopper did on the card, which the SDK saw and we can prove.
        // The backend files an event against a customer only when the request
        // carries that customer's token, and the token is ours, not the shop
        // page's — so the SDK hands the event in and we send it on.
        if (data.type === "sdk-track") {
          relayTrackedEvent(data.payload, authRef.current);
        }

        if (data.type === "sdk-send-message-quiet") {
          // Asked from the card, answered in the card. A guest cannot be
          // answered yet, but throwing the whole panel over the shop is not
          // the way to say so — the card asked, so the card is told.
          // Kept either way. Signed in, the conversation screen sends it now.
          // A guest is told in the card, and the question waits in the panel
          // and goes the moment they sign in — the card and the panel are one
          // conversation, and a question asked in one is not lost to the other.
          dispatch(setChatServicesData({ pendingMessage: data.payload?.content }));
          if (!authRef.current.isLoggedIn) {
            // Ask the backend for a guest identity first. The card is told to
            // sign in only if that is refused, or if there is no shop to ask for.
            const refuse = () =>
              postMessage({ type: "askResult", payload: { ok: false, reason: "signin" } });
            if (orgId) {
              dispatch(guestSession({ orgId })).unwrap().catch(refuse);
            } else {
              refuse();
            }
          }
          if (!history.location.pathname.startsWith("/chat-human")) {
            const orgIdParam = orgId ? `?orgId=${orgId}` : "";
            history.push(`/chat-human${orgIdParam}`);
          }
        }
      } catch (e) {
        // Non-JSON message, ignore
      }
    }
  };

  const toggleServiceModal = () => {
    if (open) {
      const message = {
        type: "dimensions",
        payload: { width: "350px", height: "160px" },
      };
      postMessage(message);
    } else {
      const message = {
        type: "dimensions",
        payload: { width: getResponsiveOpenWidth(), height: getResponsiveOpenHeight() },
      };
      postMessage(message);
    }
    dispatch(setChatServicesData({ open: !open }));
  };

  // Determine smart launcher mode based on conversation and agents
  const launcherMode = useMemo(() => {
    const recentResponses = messages
      .filter(m => m.senderType === 'AGENT' || m.senderType === 'agent' || m.senderType === 'BOT' || m.senderType === 'bot')
      .slice(-10);

    const uniqueAgents = new Set(
      recentResponses
        .filter(m => (m.senderType === 'AGENT' || m.senderType === 'agent') && m.sender?.id)
        .map(m => m.sender.id)
    );

    const hasRecentHumanResponse = recentResponses.some(m =>
      m.senderType === 'AGENT' || m.senderType === 'agent'
    );

    if (hasRecentHumanResponse) {
      if (uniqueAgents.size > 1) return 'team';
      return 'human';
    }

    if (agentsOnline && agentsOnline.length > 1) return 'team';
    if (agentsOnline && agentsOnline.length === 1) return 'human';

    return 'ai';
  }, [messages, agentsOnline]);

  // A question asked from the card is answered in the card, so the panel stays
  // shut. But the conversation screen is the only thing that actually sends a
  // message, and it exists only while this is mounted — which is why asking from
  // the card used to go nowhere. So mount it for a waiting question too, and keep
  // it out of sight: the customer goes on reading the shop while the question
  // travels. Nothing here changes `open`, so the frame is never resized and the
  // panel never appears.
  const appMounted = open || Boolean(pendingMessage);

  const app = (
    <>
      {appMounted && (
        <div
          style={open ? undefined : { display: "none" }}
          aria-hidden={open ? undefined : true}
        >
        <AppContainer>
          {isLoading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column", gap: "12px", fontFamily: "Inter, Manrope, sans-serif" }}>
              <div className="app-loading-spinner" />
              <p style={{ fontSize: "13px", color: "#888" }}>Loading...</p>
            </div>
          ) : !org?.id && !chatPreview ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column", gap: "8px", padding: "24px", textAlign: "center", fontFamily: "Inter, Manrope, sans-serif" }}>
              <p style={{ fontSize: "14px", color: "#666" }}>Unable to load chat widget.</p>
              <p style={{ fontSize: "12px", color: "#999" }}>Please check the configuration and try again.</p>
            </div>
          ) : (
            <ErrorBoundary>
              <AppRouter
                org={org}
                chatSettings={chatSettingsData}
                shopHas360={shopHas360}
                shops={shops?.content || []}
                preview={chatPreview}
              />
            </ErrorBoundary>
          )}
        </AppContainer>
        </div>
      )}
    </>
  );

  return (
    <ThemeProvider>
      <AppBase app={app} open={open} chatSettings={chatSettingsData}>
        {!open && (
          <>
            {chatSettingsData?.widgetFloating ? (
              <SideServicesApp
                onClick={toggleServiceModal}
                isLoading={isLoading}
              />
            ) : (
              <ServicesApp
                open={open}
                title={chatSettingsData?.title}
                imageUrl={chatSettingsData?.images?.logo_url}
                onClick={toggleServiceModal}
                isLoading={isLoading}
                mode={launcherMode}
              />
            )}
          </>
        )}
      </AppBase>
    </ThemeProvider>
  );
}
